import { useState, type ComponentProps } from "react";
import { useMutation } from "@tanstack/react-query";
import { Calculator, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { isCurrentAssessment } from "../financial-unavailable";
import { ServiceabilityCalculationResult } from "../panels/deal-serviceability-panel";

type DscrResult = NonNullable<ComponentProps<typeof ServiceabilityCalculationResult>["calculationResult"]>;

export interface DscrCalculatorProps {
  dealId: string;
  // Inclusion totals are selection data; they do not establish an assessment basis.
  totalIncome: number | null;
  totalCommitments: number | null;
  declaredExpenses?: number;
  householdType?: string;
  dependants?: number;
  location?: string;
  periodLabel?: string;
}

/** Only the server can establish source availability and policy conclusions. */
const ScopedDscrCalculator: React.FC<DscrCalculatorProps> = ({ dealId }) => {
  const { toast } = useToast();
  const [serverResult, setServerResult] = useState<DscrResult | null>(null);
  const calculateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/financial/calculate-serviceability", { dealId });
      return await response.json() as DscrResult;
    },
    onSuccess: result => {
      setServerResult(result);
      toast({ title: isCurrentAssessment(result) ? "Calculation complete" : "Not assessed",
        description: "Review the financial evidence and lender policy." });
    },
    onError: () => {
      toast({ title: "Calculation unavailable", description: "Check the serviceability configuration and financial inputs, then try again.", variant: "destructive" });
    },
  });
  return <Card data-testid="dscr-calculator-panel">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-sm font-semibold">Serviceability Result</CardTitle>
        <Button variant="outline" size="sm" onClick={() => calculateMutation.mutate()} disabled={calculateMutation.isPending || !dealId}>
          {calculateMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
          Calculate
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <ServiceabilityCalculationResult calculationResult={serverResult} failed={calculateMutation.isError} />
    </CardContent>
  </Card>;
};

export const DscrCalculator: React.FC<DscrCalculatorProps> = props => <ScopedDscrCalculator key={props.dealId} {...props} />;

export default DscrCalculator;
